import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Receipt, Download, FileText, Wallet, Package2, Route } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF } from "@/lib/format";

const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const DISCLAIMER =
  "Riepilogo storico degli importi registrati negli ordini, da riconciliare con DapNow. " +
  "La remunerazione dei driver interni è gestita fuori da Stripe. Questi importi non attestano " +
  "pagamenti ricevuti e non costituiscono una valutazione automatica dello status lavorativo " +
  "o degli obblighi fiscali e AVS.";

export default function DriverTaxSummary() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (y) => {
    setLoading(true);
    try {
      const res = await api.get("/driver/tax-summary", { params: { year: y } });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(year);
  }, [year]);

  const annual = data?.annual || { orders: 0, gross: 0, net_payout: 0, platform_fee: 0, distance_km: 0 };
  const months = data?.months || {};


  const downloadFile = async (kind) => {
    try {
      const url = kind === "pdf" ? "/driver/tax-summary.pdf" : "/driver/tax-summary.csv";
      const res = await api.get(url, { params: { year }, responseType: "blob" });
      const blob = new Blob([res.data], { type: kind === "pdf" ? "application/pdf" : "text/csv" });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `dapnow_compensi_${year}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(`Download ${kind.toUpperCase()} avviato`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= currentYear - 4; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  return (
    <div className="space-y-6" data-testid="driver-tax-summary-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Storico importi</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Riepilogo importi</h1>
          <p className="text-sm text-dapnow-muted mt-1">Importi annuali registrati negli ordini, da riconciliare.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="tax-year-select"
            className="input-tall px-3"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={() => downloadFile("csv")} data-testid="tax-download-csv" className="btn-ghost text-sm">
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </button>
          <button onClick={() => downloadFile("pdf")} data-testid="tax-download-pdf" className="btn-primary text-sm">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-dapnow-line bg-dapnow-bg p-5 text-sm text-dapnow-muted" data-testid="tax-reconciliation-note">
        Importi storici da riconciliare. Per il riepilogo della remunerazione interna rivolgiti al referente DapNow.
      </div>

      <section className="grid sm:grid-cols-4 gap-4">
        <KpiCard icon={Package2} tone="blue" label="Consegne" value={annual.orders} />
        <KpiCard icon={Wallet} tone="violet" label="Lordo registrato" value={formatCHF(annual.gross)} />
        <KpiCard icon={Wallet} tone="emerald" label="Netto registrato" value={formatCHF(annual.net_payout)} highlight />
        <KpiCard icon={Route} tone="muted" label="Km stimati" value={`${Number(annual.distance_km).toFixed(1)} km`} />
      </section>

      <section className="card-soft p-6" data-testid="tax-monthly-table">
        <div className="flex items-center justify-between">
          <div>
            <p className="overline">Dettaglio mensile</p>
            <h2 className="font-display text-lg font-bold tracking-tight">Importi {year}</h2>
          </div>
          <span className="text-xs text-dapnow-muted">In CHF</span>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-dapnow-muted border-b border-dapnow-line">
                <th className="py-2 pr-3 font-semibold">Mese</th>
                <th className="py-2 px-3 font-semibold text-right">Consegne</th>
                <th className="py-2 px-3 font-semibold text-right">Lordo</th>
                <th className="py-2 px-3 font-semibold text-right">Commissione</th>
                <th className="py-2 px-3 font-semibold text-right">Netto</th>
                <th className="py-2 pl-3 font-semibold text-right">Km</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 12 }).map((_, i) => {
                const m = months[i + 1] || months[String(i + 1)] || {
                  orders: 0, gross: 0, platform_fee: 0, net_payout: 0, distance_km: 0,
                };
                const isEmpty = !m.orders;
                return (
                  <tr key={i} className={`border-b border-dapnow-line/60 last:border-0 ${isEmpty ? "text-dapnow-muted" : ""}`}>
                    <td className="py-2.5 pr-3 font-semibold">{MONTH_LABELS[i]} {year}</td>
                    <td className="py-2.5 px-3 text-right">{m.orders}</td>
                    <td className="py-2.5 px-3 text-right">{formatCHF(m.gross)}</td>
                    <td className="py-2.5 px-3 text-right">{formatCHF(m.platform_fee)}</td>
                    <td className="py-2.5 px-3 text-right font-semibold">{formatCHF(m.net_payout)}</td>
                    <td className="py-2.5 pl-3 text-right">{Number(m.distance_km || 0).toFixed(1)}</td>
                  </tr>
                );
              })}
              <tr className="bg-dapnow-bg/60">
                <td className="py-3 pr-3 font-display font-bold">Totale {year}</td>
                <td className="py-3 px-3 text-right font-bold">{annual.orders}</td>
                <td className="py-3 px-3 text-right font-bold">{formatCHF(annual.gross)}</td>
                <td className="py-3 px-3 text-right font-bold">{formatCHF(annual.platform_fee)}</td>
                <td className="py-3 px-3 text-right font-bold text-dapnow-blue">{formatCHF(annual.net_payout)}</td>
                <td className="py-3 pl-3 text-right font-bold">{Number(annual.distance_km).toFixed(1)}</td>
              </tr>
            </tbody>
          </table>
          {loading && <p className="text-center text-sm text-dapnow-muted py-4">Caricamento…</p>}
        </div>
      </section>

      <section className="rounded-2xl border border-dapnow-line bg-white p-5" data-testid="tax-disclaimer">
        <div className="flex items-start gap-3">
          <Receipt className="h-5 w-5 text-dapnow-muted shrink-0 mt-0.5" />
          <p className="text-xs text-dapnow-muted leading-relaxed">{DISCLAIMER}</p>
        </div>
      </section>
    </div>
  );
}

function KpiCard({ icon: Icon, tone = "blue", label, value, highlight }) {
  const toneCls = {
    blue: "bg-dapnow-blue-soft text-dapnow-blue",
    emerald: "bg-emerald-50 text-emerald-700",
    violet: "bg-violet-50 text-violet-700",
    muted: "bg-dapnow-bg text-dapnow-muted",
  }[tone];
  return (
    <div className={`card-soft p-6 ${highlight ? "ring-2 ring-emerald-500/40" : ""}`}>
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${toneCls}`}>
        <Icon className="h-5 w-5" />
      </span>
      <p className="overline mt-4">{label}</p>
      <p className="font-display text-3xl font-extrabold tracking-tight mt-1">{value}</p>
    </div>
  );
}
